// ===========================================
// Détail Compte Client — Refonte UX (CRM layout : sidebar + KPIs + tableau)
// ===========================================
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
    Card, Table, Spin, Button, Grid, Tag, Empty, Dropdown, Switch, Typography,
} from 'antd';
import {
    ArrowLeftOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined,
    BankOutlined, IdcardOutlined, DownloadOutlined, FilePdfOutlined,
    FileExcelOutlined, PrinterOutlined, FileProtectOutlined, CheckCircleOutlined,
    WarningOutlined, UserOutlined, DollarOutlined, CreditCardOutlined,
    WalletOutlined, SwapOutlined, MobileOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { clientsService } from '../../common/services';
import { useExporterName } from '../../common/hooks';
import { addExporterFooter } from '../../common/utils/pdfExport';
import jsPDF from 'jspdf';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import logoCap from '../../assets/logo/logo-cap-assurances.JPG';
import { formatMontant, formatDate as formatDateShared, formatPhone } from '../../common/utils/format';

import MiniKpiCard from '../clients/components/MiniKpiCard';

import './etat-client-detail.page.scss';

const { Text } = Typography;

const fmt = (v) => formatMontant(v);
const formatDate = (d) => formatDateShared(d, { fallback: '-' });

const moyenPaiementConfig = {
    especes:      { label: 'Espèces',      color: '#16a34a', bg: '#f6ffed', icon: <WalletOutlined /> },
    cheque:       { label: 'Chèque',       color: '#1890ff', bg: '#e6f7ff', icon: <CreditCardOutlined /> },
    virement:     { label: 'Virement',     color: '#7c3aed', bg: '#f9f0ff', icon: <SwapOutlined /> },
    mobile_money: { label: 'Mobile Money', color: '#fa8c16', bg: '#fff7e6', icon: <MobileOutlined /> },
};

const EtatClientDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const exporterName = useExporterName();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showOperateur, setShowOperateur] = useState(false);
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await clientsService.getSituation(id);
                setData(res?.data || res);
            } catch (error) {
                toast.error('Erreur lors du chargement des données');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    if (loading) {
        return (
            <div className="etat-client-detail-loading">
                <Spin size="large" tip="Chargement de la situation..." />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="etat-client-detail-empty">
                <Empty description="Client introuvable" />
                <Button type="primary" onClick={() => navigate(-1)} style={{ marginTop: 16 }}>Retour</Button>
            </div>
        );
    }

    const client = data.client || {};
    const clientName = client.type === 'entreprise'
        ? (client.raisonSociale || client.nom || '')
        : `${client.nom || ''} ${client.prenom || ''}`.trim();
    const initials = clientName.split(' ').filter(Boolean).map(w => w[0]).join('').substring(0, 2).toUpperCase();
    const isEntreprise = client.type === 'entreprise';
    const soldeGlobal = data.soldeGlobal || 0;
    const hasSolde = soldeGlobal > 0;

    // ─── Aplatissement des règlements pour le tableau (rowSpan préservé) ───
    const allReglements = [];
    (data.situationParContrat || []).forEach(item => {
        const contrat = item.contrat || {};
        if (item.reglements && item.reglements.length > 0) {
            item.reglements.forEach((reg, idx) => {
                allReglements.push({
                    ...reg,
                    _contratCode: contrat.numeroPolice || '-',
                    _contratType: contrat.typeAssurance?.nom || contrat.typeAssurance || '-',
                    _primeTTC: contrat.primeTTC || 0,
                    _totalVerseContrat: item.totalVerse || 0,
                    _resteContrat: item.resteAPayer || 0,
                    _statut: contrat.statut || '-',
                    _isFirstRow: idx === 0,
                    _rowSpan: idx === 0 ? item.reglements.length : 0,
                });
            });
        } else {
            allReglements.push({
                _id: `no-reg-${contrat._id}`,
                _contratCode: contrat.numeroPolice || '-',
                _contratType: contrat.typeAssurance?.nom || contrat.typeAssurance || '-',
                _primeTTC: contrat.primeTTC || 0,
                _totalVerseContrat: 0,
                _resteContrat: item.resteAPayer || contrat.primeTTC || 0,
                _statut: contrat.statut || '-',
                montant: 0,
                dateReglement: null,
                moyenPaiement: null,
                reference: null,
                _noReglement: true,
                _isFirstRow: true,
                _rowSpan: 1,
            });
        }
    });

    const mergedCell = (record) => ({ rowSpan: record._rowSpan });

    const statutTag = (s) => {
        const conf = {
            actif:     { color: 'green',   label: 'Actif' },
            expire:    { color: 'red',     label: 'Expiré' },
            resilie:   { color: 'volcano', label: 'Résilié' },
            renouvele: { color: 'blue',    label: 'Renouvelé' },
            suspendu:  { color: 'orange',  label: 'Suspendu' },
        };
        const c = conf[s] || { color: 'default', label: s };
        return <Tag color={c.color} className="etat-client-detail-tag">{c.label}</Tag>;
    };

    const columns = [
        {
            title: 'N° Police',
            dataIndex: '_contratCode',
            key: 'police',
            width: 160,
            fixed: isMobile ? false : 'left',
            onCell: mergedCell,
            render: (t) => <span className="etat-client-detail-police">{t}</span>,
        },
        {
            title: 'Type',
            dataIndex: '_contratType',
            key: 'type',
            width: 180,
            onCell: mergedCell,
            render: (t) => <Tag color="geekblue" className="etat-client-detail-tag">{t}</Tag>,
        },
        {
            title: 'Statut',
            dataIndex: '_statut',
            key: 'statut',
            width: 110,
            onCell: mergedCell,
            render: statutTag,
        },
        {
            title: 'Prime TTC',
            dataIndex: '_primeTTC',
            key: 'prime',
            width: 140,
            align: 'right',
            onCell: mergedCell,
            render: (v) => <span className="etat-client-detail-amount">{fmt(v)}</span>,
        },
        {
            title: 'Total payé',
            dataIndex: '_totalVerseContrat',
            key: 'totalPaye',
            width: 140,
            align: 'right',
            onCell: mergedCell,
            render: (v) => <span className="etat-client-detail-amount is-positive">{fmt(v)}</span>,
        },
        {
            title: 'Solde',
            dataIndex: '_resteContrat',
            key: 'solde',
            width: 130,
            align: 'right',
            onCell: mergedCell,
            render: (v) => (
                <span className={`etat-client-detail-amount ${v > 0 ? 'is-warning' : 'is-positive'}`}>
                    {fmt(v)}
                </span>
            ),
        },
        {
            title: 'Versé',
            dataIndex: 'montant',
            key: 'verse',
            width: 130,
            align: 'right',
            render: (v, r) => r._noReglement
                ? <span className="etat-client-detail-muted">-</span>
                : <span className="etat-client-detail-amount is-positive">{fmt(v)}</span>,
        },
        {
            title: 'Date règlement',
            dataIndex: 'dateReglement',
            key: 'date',
            width: 130,
            render: (d, r) => r._noReglement ? '-' : formatDate(d),
            sorter: (a, b) => new Date(a.dateReglement || 0) - new Date(b.dateReglement || 0),
            defaultSortOrder: 'descend',
        },
        {
            title: 'Moyen',
            dataIndex: 'moyenPaiement',
            key: 'moyen',
            width: 140,
            render: (m, r) => {
                if (r._noReglement || !m) return '-';
                const conf = moyenPaiementConfig[m] || { label: m, color: '#64748b', bg: '#f8fafc', icon: null };
                return (
                    <Tag
                        icon={conf.icon}
                        className="etat-client-detail-tag"
                        style={{ backgroundColor: conf.bg, color: conf.color, border: `1px solid ${conf.color}30` }}
                    >
                        {conf.label}
                    </Tag>
                );
            },
        },
        {
            title: 'Référence',
            dataIndex: 'reference',
            key: 'ref',
            width: 160,
            ellipsis: true,
            render: (t, r) => r._noReglement ? '-' : (
                <span className="etat-client-detail-ref">{t || '-'}</span>
            ),
        },
        {
            title: 'Opérateur',
            dataIndex: 'enregistrePar',
            key: 'operateur',
            width: 140,
            render: (u, r) => {
                if (r._noReglement) return '-';
                if (!u) return '-';
                if (typeof u === 'string') return u;
                return `${u.firstName || ''} ${u.lastName || ''}`.trim() || '-';
            },
        },
    ].filter(col => col.key !== 'operateur' || showOperateur);

    const renderSummary = () => {
        if (!data.situationParContrat?.length) return null;
        return (
            <Table.Summary fixed>
                <Table.Summary.Row className="etat-client-detail-summary">
                    <Table.Summary.Cell index={0} colSpan={3}>
                        <strong>TOTAUX</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                        <strong className="etat-client-detail-amount">{fmt(data.totalPrimesTTC)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                        <strong className="etat-client-detail-amount is-positive">{fmt(data.totalVersements)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                        <strong className={`etat-client-detail-amount ${hasSolde ? 'is-warning' : 'is-positive'}`}>
                            {fmt(soldeGlobal)}
                        </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">
                        <strong className="etat-client-detail-amount is-positive">{fmt(data.totalVersements)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7} colSpan={showOperateur ? 4 : 3} />
                </Table.Summary.Row>
            </Table.Summary>
        );
    };

    // ─── Exports (logique préservée) ─────────────────────────────────────

    const handleExportPDF = () => {
        const doc = new jsPDF({ orientation: 'landscape' });
        const today = new Date().toLocaleDateString('fr-FR');
        const pageWidth = doc.internal.pageSize.width;
        const centerX = pageWidth / 2;

        doc.addImage(logoCap, 'JPEG', 14, 8, 40, 16);

        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text(today, pageWidth - 14, 15, { align: 'right' });

        doc.setFontSize(15);
        doc.setFont(undefined, 'bolditalic');
        doc.setTextColor(26, 43, 95);
        doc.text('DETAILS DE COMPTE', centerX, 32, { align: 'center' });
        doc.setFont(undefined, 'normal');

        doc.setFontSize(11);
        doc.setTextColor(0);
        doc.text(`Client : ${clientName.toUpperCase()}`, 14, 42);
        doc.setFontSize(9);
        doc.text(`Code : ${client.code || 'N/A'}   |   Type : ${(client.type || '').toUpperCase()}   |   Tel : ${formatPhone(client.telephone)}   |   Agence : ${client.agence?.nom || 'N/A'}`, 14, 48);

        const pdfFmt = (v) => (v || 0).toLocaleString('fr-FR').replace(/\u202F/g, ' ').replace(/\u00A0/g, ' ');

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(`MONTANT TOTAL PRIME :  ${pdfFmt(data.totalPrimesTTC)}`, 14, 58);
        doc.text(`MONTANT PAYE :  ${pdfFmt(data.totalVersements)}`, centerX - 30, 58);
        doc.text(`SOLDE :  ${pdfFmt(soldeGlobal)}`, pageWidth - 80, 58);
        doc.setFont(undefined, 'normal');

        const contratGroups = (data.situationParContrat || []).map(item => {
            const c = item.contrat || {};
            const regs = (item.reglements && item.reglements.length > 0) ? item.reglements : [null];
            return {
                police: c.numeroPolice || '-',
                type: c.typeAssurance?.nom || c.typeAssurance || '-',
                prime: pdfFmt(c.primeTTC),
                totalPaye: pdfFmt(item.totalVerse),
                solde: pdfFmt(item.resteAPayer),
                rowCount: regs.length,
                reglements: regs.map(reg => {
                    if (!reg) return { verse: '-', date: '-', moyen: '-', ref: '-', operateur: '-' };
                    const op = typeof reg.enregistrePar === 'object'
                        ? `${reg.enregistrePar?.firstName || ''} ${reg.enregistrePar?.lastName || ''}`.trim()
                        : (reg.enregistrePar || '-');
                    return {
                        verse: pdfFmt(reg.montant),
                        date: formatDate(reg.dateReglement),
                        moyen: moyenPaiementConfig[reg.moyenPaiement]?.label || reg.moyenPaiement || '-',
                        ref: reg.reference || '-',
                        operateur: op || '-',
                    };
                }),
            };
        });

        const startY = 64;
        const marginLeft = 14;
        const rowHeight = 14;
        const colWidths = showOperateur
            ? [34, 36, 22, 22, 22, 22, 22, 20, 24, 24]
            : [38, 40, 24, 24, 24, 24, 24, 22, 28];
        const headers = showOperateur
            ? ['N° Police', 'Type', 'Prime TTC', 'Total paye', 'Solde', 'Verse', 'Date regl.', 'Moyen', 'Référence', 'Opérateur']
            : ['N° Police', 'Type', 'Prime TTC', 'Total paye', 'Solde', 'Verse', 'Date regl.', 'Moyen', 'Référence'];
        const nbPerRegCols = showOperateur ? 5 : 4;

        doc.setDrawColor(0);
        doc.setLineWidth(0.3);
        doc.setFontSize(8);

        let x = marginLeft;
        doc.setFont(undefined, 'bold');
        headers.forEach((h, i) => {
            doc.rect(x, startY, colWidths[i], rowHeight);
            doc.text(h, x + colWidths[i] / 2, startY + rowHeight / 2 + 1, { align: 'center', baseline: 'middle' });
            x += colWidths[i];
        });

        let curY = startY + rowHeight;
        doc.setFont(undefined, 'normal');

        contratGroups.forEach(group => {
            const groupHeight = group.rowCount * rowHeight;

            x = marginLeft;
            const mergedData = [group.police, group.type, group.prime, group.totalPaye, group.solde];
            for (let col = 0; col < 5; col++) {
                doc.rect(x, curY, colWidths[col], groupHeight);
                const txt = mergedData[col] || '';
                const align = col >= 2 ? 'right' : 'left';
                const textX = align === 'right' ? x + colWidths[col] - 3 : x + 3;
                doc.text(txt, textX, curY + groupHeight / 2 + 1, { align, baseline: 'middle' });
                x += colWidths[col];
            }

            const perRegStartX = marginLeft + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4];
            group.reglements.forEach((reg, rIdx) => {
                const regY = curY + rIdx * rowHeight;
                let cx = perRegStartX;
                const regData = showOperateur
                    ? [reg.verse, reg.date, reg.moyen, reg.ref, reg.operateur]
                    : [reg.verse, reg.date, reg.moyen, reg.ref];
                for (let ri = 0; ri < nbPerRegCols; ri++) {
                    doc.rect(cx, regY, colWidths[5 + ri], rowHeight);
                    const align = ri === 0 ? 'right' : 'left';
                    const textX = align === 'right' ? cx + colWidths[5 + ri] - 3 : cx + 3;
                    doc.text(regData[ri], textX, regY + rowHeight / 2 + 1, { align, baseline: 'middle' });
                    cx += colWidths[5 + ri];
                }
            });

            curY += groupHeight;
        });

        const totalY = curY;
        doc.setFont(undefined, 'bold');
        x = marginLeft;
        x += colWidths[0];
        doc.rect(x, totalY, colWidths[1], rowHeight);
        doc.text('TOTAL', x + colWidths[1] - 3, totalY + rowHeight / 2 + 1, { align: 'right', baseline: 'middle' });
        x += colWidths[1];
        doc.rect(x, totalY, colWidths[2], rowHeight);
        doc.text(pdfFmt(data.totalPrimesTTC), x + colWidths[2] - 3, totalY + rowHeight / 2 + 1, { align: 'right', baseline: 'middle' });
        x += colWidths[2];
        doc.rect(x, totalY, colWidths[3], rowHeight);
        doc.text(pdfFmt(data.totalVersements), x + colWidths[3] - 3, totalY + rowHeight / 2 + 1, { align: 'right', baseline: 'middle' });
        x += colWidths[3];
        doc.rect(x, totalY, colWidths[4], rowHeight);
        doc.text(pdfFmt(soldeGlobal), x + colWidths[4] - 3, totalY + rowHeight / 2 + 1, { align: 'right', baseline: 'middle' });
        x += colWidths[4];
        doc.rect(x, totalY, colWidths[5], rowHeight);
        doc.text(pdfFmt(data.totalVersements), x + colWidths[5] - 3, totalY + rowHeight / 2 + 1, { align: 'right', baseline: 'middle' });
        doc.setFont(undefined, 'normal');

        const pageCount = doc.internal.getNumberOfPages();
        const pageHeight = doc.internal.pageSize.height;
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setDrawColor(0, 0, 0);
            doc.setLineWidth(1);
            doc.line(14, pageHeight - 28, pageWidth - 14, pageHeight - 28);
            doc.setFontSize(8);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text('Cocody 2 Plateaux Vallon ; Rue des Jardins, 28 BP 56 Abidjan 28 / Tel: (+225) 07 10 01 60 54 / 07 88 47 17 79 / 27 22 41 93 50', pageWidth / 2, pageHeight - 20, { align: 'center' });
            doc.setFont(undefined, 'normal');
            doc.setFontSize(7);
            doc.text('RCCM: CI-ABJ-03-2024-B13-023771 | NCC: 2400889R | infos@capassurances.com', pageWidth / 2, pageHeight - 14, { align: 'center' });
        }

        addExporterFooter(doc, exporterName);
        doc.save(`detail_compte_${clientName.replace(/\s/g, '_')}_${today.replace(/\//g, '-')}.pdf`);
        toast.success('PDF exporté avec succès');
    };

    const handleExportExcel = async () => {
        const today = new Date().toLocaleDateString('fr-FR');
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Détail Compte');
        ws.columns = [
            { width: 22 }, { width: 16 }, { width: 16 }, { width: 16 }, { width: 16 },
            { width: 16 }, { width: 14 }, { width: 14 }, { width: 20 }, { width: 16 },
        ];

        const logoRes = await fetch(logoCap);
        const logoBlob = await logoRes.blob();
        const logoBuffer = await logoBlob.arrayBuffer();
        const logoId = wb.addImage({ buffer: logoBuffer, extension: 'jpeg' });
        ws.addImage(logoId, { tl: { col: 0, row: 0 }, ext: { width: 160, height: 60 } });
        ws.addRow([]); ws.addRow([]); ws.addRow([]);

        const titleRow = ws.addRow([`DETAILS DE COMPTE - ${clientName.toUpperCase()}`]);
        ws.mergeCells(4, 1, 4, 10);
        titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: '1A2B5F' } };
        titleRow.getCell(1).alignment = { horizontal: 'center' };

        const infoRow = ws.addRow([`Code: ${client.code || 'N/A'}  |  Type: ${(client.type || '').toUpperCase()}  |  Agence: ${client.agence?.nom || 'N/A'}  |  Exporté le ${today}${exporterName ? `  |  Exporté par : ${exporterName}` : ''}`]);
        ws.mergeCells(5, 1, 5, 10);
        infoRow.getCell(1).font = { italic: true, size: 10, color: { argb: '666666' } };
        infoRow.getCell(1).alignment = { horizontal: 'center' };
        ws.addRow([]);

        const sumRow = ws.addRow(['TOTAL PRIMES', fmt(data.totalPrimesTTC), '', 'TOTAL PAYE', fmt(data.totalVersements), '', 'SOLDE', fmt(soldeGlobal)]);
        sumRow.eachCell(cell => { cell.font = { bold: true, size: 11 }; });
        ws.addRow([]);

        const headers = ['N° Police', 'Type', 'Prime TTC', 'Verse', 'Total paye', 'Solde', 'Date regl.', 'Moyen', 'Référence', 'Opérateur'];
        const headerRow = ws.addRow(headers);
        headerRow.eachCell(cell => {
            cell.font = { bold: true, color: { argb: 'FFFFFF' }, size: 10 };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1A2B5F' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
        });

        allReglements.forEach(r => {
            const operateur = typeof r.enregistrePar === 'object' ? `${r.enregistrePar?.firstName || ''} ${r.enregistrePar?.lastName || ''}`.trim() : (r.enregistrePar || '');
            const row = ws.addRow([
                r._contratCode,
                r._contratType,
                r._primeTTC || 0,
                r._noReglement ? 0 : (r.montant || 0),
                r._totalVerseContrat || 0,
                r._resteContrat || 0,
                r._noReglement ? '' : formatDate(r.dateReglement),
                r._noReglement ? '' : (moyenPaiementConfig[r.moyenPaiement]?.label || r.moyenPaiement || ''),
                r._noReglement ? '' : (r.reference || ''),
                r._noReglement ? '' : operateur,
            ]);
            row.eachCell((cell, colNumber) => {
                cell.border = { top: { style: 'thin', color: { argb: 'DDDDDD' } }, bottom: { style: 'thin', color: { argb: 'DDDDDD' } }, left: { style: 'thin', color: { argb: 'DDDDDD' } }, right: { style: 'thin', color: { argb: 'DDDDDD' } } };
                if (colNumber >= 3 && colNumber <= 6) {
                    cell.numFmt = '#,##0';
                    cell.alignment = { horizontal: 'right' };
                }
                if (colNumber === 6 && r._resteContrat > 0) {
                    cell.font = { bold: true, color: { argb: 'C8102E' } };
                }
            });
        });

        const totalRow = ws.addRow(['', 'TOTAL', data.totalPrimesTTC, '', data.totalVersements, soldeGlobal, '', '', '', '']);
        totalRow.eachCell((cell, col) => {
            cell.font = { bold: true, size: 11 };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'EEF0F8' } };
            cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
            if (col >= 3 && col <= 6) cell.numFmt = '#,##0';
        });

        const buffer = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `detail_compte_${clientName.replace(/\s/g, '_')}_${today.replace(/\//g, '-')}.xlsx`);
        toast.success('Excel exporté avec succès');
    };

    const exportMenuItems = [
        { key: 'pdf',   label: 'Exporter en PDF',   icon: <FilePdfOutlined style={{ color: '#C8102E' }} />, onClick: handleExportPDF },
        { key: 'excel', label: 'Exporter en Excel', icon: <FileExcelOutlined style={{ color: '#16a34a' }} />, onClick: handleExportExcel },
    ];

    return (
        <div className="etat-client-detail-page">
            <div className="etat-client-detail-layout">
                {/* ═══════════════ SIDEBAR ═══════════════ */}
                <aside className="etat-client-detail-sidebar">
                    <Card className="etat-client-detail-sidebar-card">
                        <button
                            type="button"
                            className="sidebar-back-btn"
                            onClick={() => navigate('/dashboard/etat-clients')}
                        >
                            <ArrowLeftOutlined /> Retour à l'état clients
                        </button>

                        <div className="sidebar-identity">
                            <div className={`sidebar-client-avatar ${isEntreprise ? 'is-entreprise' : 'is-particulier'}`}>
                                {initials || <UserOutlined />}
                            </div>
                            <h2 className="sidebar-name">{clientName || '—'}</h2>
                            <div className="sidebar-meta">
                                <Tag color={isEntreprise ? 'orange' : 'blue'} className="sidebar-type-tag">
                                    {isEntreprise ? 'ENTREPRISE' : 'PARTICULIER'}
                                </Tag>
                                {client.code && (
                                    <Tag icon={<IdcardOutlined />} className="sidebar-code-tag">
                                        {client.code}
                                    </Tag>
                                )}
                            </div>
                        </div>

                        <div className="sidebar-divider" />

                        <div className="sidebar-coord">
                            {client.telephone && (
                                <div className="sidebar-coord-item">
                                    <div className="sidebar-coord-icon"><PhoneOutlined /></div>
                                    <div className="sidebar-coord-body">
                                        <div className="sidebar-coord-label">Téléphone</div>
                                        <a href={`tel:${client.telephone}`} className="sidebar-coord-value sidebar-coord-link">
                                            {formatPhone(client.telephone)}
                                        </a>
                                    </div>
                                </div>
                            )}
                            {client.email && (
                                <div className="sidebar-coord-item">
                                    <div className="sidebar-coord-icon"><MailOutlined /></div>
                                    <div className="sidebar-coord-body">
                                        <div className="sidebar-coord-label">Email</div>
                                        <a href={`mailto:${client.email}`} className="sidebar-coord-value sidebar-coord-link">
                                            {client.email}
                                        </a>
                                    </div>
                                </div>
                            )}
                            {(client.adresse || client.ville) && (
                                <div className="sidebar-coord-item">
                                    <div className="sidebar-coord-icon"><EnvironmentOutlined /></div>
                                    <div className="sidebar-coord-body">
                                        <div className="sidebar-coord-label">Adresse</div>
                                        <div className="sidebar-coord-value">
                                            {[client.adresse, client.ville].filter(Boolean).join(', ') || '—'}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="sidebar-coord-item">
                                <div className="sidebar-coord-icon"><BankOutlined /></div>
                                <div className="sidebar-coord-body">
                                    <div className="sidebar-coord-label">Agence</div>
                                    <div className="sidebar-coord-value">{client.agence?.nom || '—'}</div>
                                </div>
                            </div>
                        </div>

                        <div className="sidebar-divider" />

                        <div className="sidebar-actions">
                            <Button
                                icon={<PrinterOutlined />}
                                onClick={handleExportPDF}
                                block
                                className="sidebar-btn-print"
                            >
                                Imprimer
                            </Button>
                            <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight" trigger={['click']}>
                                <Button
                                    type="primary"
                                    icon={<DownloadOutlined />}
                                    block
                                    className="sidebar-btn-export"
                                >
                                    Exporter
                                </Button>
                            </Dropdown>
                        </div>
                    </Card>
                </aside>

                {/* ═══════════════ MAIN ═══════════════ */}
                <div className="etat-client-detail-main">
                    {/* KPIs */}
                    <section className="etat-client-detail-kpis" aria-label="Indicateurs compte client">
                        <MiniKpiCard
                            icon={<FileProtectOutlined />}
                            label="Montant total prime"
                            value={fmt(data.totalPrimesTTC || 0)}
                            tone="blue"
                        />
                        <MiniKpiCard
                            icon={<CheckCircleOutlined />}
                            label="Montant payé"
                            value={fmt(data.totalVersements || 0)}
                            tone="green"
                        />
                        <MiniKpiCard
                            icon={<WarningOutlined />}
                            label="Solde"
                            value={fmt(soldeGlobal)}
                            tone={hasSolde ? 'red' : 'green'}
                        />
                    </section>

                    {/* Carte tableau */}
                    <Card className="etat-client-detail-card">
                        <div className="etat-client-detail-card-header">
                            <div className="etat-client-detail-card-header-text">
                                <h3 className="etat-client-detail-card-title">
                                    <DollarOutlined /> Détails des contrats et règlements
                                </h3>
                                <p className="etat-client-detail-card-subtitle">
                                    Historique complet des opérations financières
                                </p>
                            </div>
                            <div className="etat-client-detail-card-header-actions">
                                <div className="etat-client-detail-switch">
                                    <Switch
                                        checked={showOperateur}
                                        onChange={setShowOperateur}
                                        size="small"
                                    />
                                    <span className="etat-client-detail-switch-label">Afficher opérateur</span>
                                </div>
                                <Tag color="blue" className="etat-client-detail-count-tag">
                                    {allReglements.length} ligne(s)
                                </Tag>
                            </div>
                        </div>

                        <Table
                            dataSource={allReglements}
                            columns={columns}
                            rowKey={(r) => r._id || `${r._contratCode}-${Math.random()}`}
                            size="small"
                            scroll={{ x: 1500 }}
                            locale={{ emptyText: <Empty description="Aucune donnée" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                            pagination={allReglements.length > 20 ? {
                                pageSize: 20,
                                showTotal: (t) => <Text strong>{t} ligne(s)</Text>,
                                showSizeChanger: true,
                                size: 'small',
                            } : false}
                            summary={renderSummary}
                            rowClassName={(record) => (record._resteContrat > 0 && record._isFirstRow) ? 'row-solde-impaye' : ''}
                        />
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default EtatClientDetail;
